import React from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useCollectionBinding } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

interface Review {
  name?: string | null;
  title?: string | null;
  rating?: string | number | null;
  text?: string | null;
  date?: string | null;
  source?: string | null;
}

function hasValue(val: unknown): val is string {
  if (val == null) return false;
  const s = String(val).trim().toLowerCase();
  return s !== '' && s !== 'false' && s !== 'null' && s !== 'undefined';
}

function abbreviateName(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length <= 1) return parts[0] || name;
  return `${parts[0]} ${parts[parts.length - 1][0]}.`;
}

const APARTMENTS_ICON = 'https://console.multihub.io/_next/image?url=https%3A%2F%2Fstorage.googleapis.com%2Fmultihub-assets%2Flogos%2Fapartments.png&w=48&q=75';

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09A6.97 6.97 0 015.47 12c0-.72.13-1.43.37-2.09V7.07H2.18A11.96 11.96 0 001 12c0 1.94.46 3.77 1.18 5.07l3.66-2.98z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 1.09 14.97 0 12 0 7.7 0 3.99 2.47 2.18 6.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  );
}

function SourceBadge({ source }: { source: string }) {
  const normalized = source.trim().toLowerCase();

  if (normalized.includes('apartments.com') || normalized.includes('apartments')) {
    return (
      <span className="inline-flex items-center gap-1.5">
        <VibesitesImage url={APARTMENTS_ICON} alt="" className="w-4 h-4 object-contain" aria-hidden="true" />
        <span>Apartments.com</span>
      </span>
    );
  }

  if (normalized.includes('google')) {
    return (
      <span className="inline-flex items-center gap-1.5">
        <GoogleIcon className="w-4 h-4" />
        <span>Google</span>
      </span>
    );
  }

  if (normalized === 'custom') return null;

  return <span>{source}</span>;
}

function renderStars(rating: number = 5) {
  return Array.from({ length: 5 }, (_, i) => (
    <svg
      key={i}
      className={`w-4 h-4 ${i < rating ? 'text-[var(--vibe-color-primary)]' : 'text-[var(--vibe-color-border)]'}`}
      fill="currentColor"
      viewBox="0 0 20 20"
    >
      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
    </svg>
  ));
}

export function Reviews() {
  const rawReviews = useCollectionBinding<Review>('reviews.items', {
    collection: 'reviews',
  });

  const reviews = rawReviews
    ? [...rawReviews].sort((a, b) => {
        const da = a.date ? new Date(a.date).getTime() : 0;
        const db = b.date ? new Date(b.date).getTime() : 0;
        return db - da;
      })
    : null;

  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {reviews && reviews.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reviews.map((review, index) => {
              const numericRating = typeof review.rating === 'string' ? parseInt(review.rating, 10) : review.rating;

              return (
                <div
                  key={index}
                  className="p-6 bg-white border border-[var(--vibe-color-border)] flex flex-col"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {renderStars(numericRating ?? 5)}
                  </div>
                  {hasValue(review.text) && (
                    <p className="font-body text-sm text-[var(--vibe-color-text)] mb-4 flex-grow leading-relaxed">
                      &ldquo;{review.text}&rdquo;
                    </p>
                  )}
                  <div className="mt-auto pt-4 border-t border-[var(--vibe-color-border)]">
                    {hasValue(review.name) && (
                      <p className="font-body text-sm font-semibold text-[var(--vibe-color-text)]">
                        {abbreviateName(review.name!)}
                      </p>
                    )}
                    <div className="flex items-center gap-2 text-xs text-[var(--vibe-color-text-muted)]">
                      {hasValue(review.source) && <SourceBadge source={review.source!} />}
                      {hasValue(review.date) && <span>{review.date}</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12 text-[var(--vibe-color-text-muted)]">
            <p className="font-body text-lg">Reviews will be displayed here.</p>
          </div>
        )}
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Reviews',
  displayName: 'Reviews',
  tokens: {},
  collectionBindings: {
    'reviews.items': {
      label: 'Reviews',
      collection: 'reviews',
    },
  },
};

(Reviews as any).contentSchema = contentSchema;

export default Reviews;
